/**
 * Batched analytics client. Owns three client-side queues (events, AI
 * feedback, bug reports), flushes them to the first-party backend on a cadence
 * / size threshold / page-hide, and fans a privacy-guarded subset out to
 * PostHog.
 *
 * The client is resilient: a missing transport simply buffers (so `track`
 * called before init is not lost), and all network paths swallow errors —
 * analytics must never throw into product code.
 */

import { PostHogFanout } from './posthog'
import type {
  AiFeedbackInput,
  AnalyticsClientConfig,
  AnalyticsTransport,
  BugReportInput,
  EventsIngestRequest,
  PostHogConfig,
  TrackInput,
} from './types'
import type {
  FgAiFeedbackPayload,
  FgBugReportPayload,
  FgTrackEventPayload,
} from '../data/ServerApiClient'

const DEFAULT_BATCH_SIZE = 20
const DEFAULT_FLUSH_INTERVAL_MS = 5000
/** Hard cap so a persistently-down backend cannot grow the queue unbounded. */
const MAX_QUEUE = 500

function genSessionId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return `s_${crypto.randomUUID()}`
    }
  } catch {
    /* ignore */
  }
  return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
}

export class AnalyticsClient {
  private transport: AnalyticsTransport | null
  private posthog: PostHogFanout | null
  private readonly batchSize: number
  private readonly flushIntervalMs: number
  private readonly sessionId: string
  private readonly now: () => number

  private events: FgTrackEventPayload[] = []
  private feedback: FgAiFeedbackPayload[] = []
  private bugs: FgBugReportPayload[] = []

  private timer: ReturnType<typeof setInterval> | null = null
  private flushing: Promise<void> | null = null
  private listenersBound = false
  private readonly boundFlush = () => {
    void this.flush()
  }

  constructor(config: AnalyticsClientConfig = {}) {
    this.transport = config.transport ?? null
    this.posthog = PostHogFanout.fromConfig(config.posthog)
    this.batchSize = Math.max(1, Math.floor(config.batchSize ?? DEFAULT_BATCH_SIZE))
    this.flushIntervalMs = Math.max(250, Math.floor(config.flushIntervalMs ?? DEFAULT_FLUSH_INTERVAL_MS))
    this.sessionId = config.sessionId || genSessionId()
    this.now = config.now ?? (() => Date.now())
    this.start()
  }

  get session(): string {
    return this.sessionId
  }

  setTransport(transport: AnalyticsTransport | null): void {
    this.transport = transport
    if (transport) void this.flush()
  }

  setPostHog(config: PostHogConfig | null): void {
    this.posthog = PostHogFanout.fromConfig(config)
  }

  setUser(distinctId: string | null | undefined): void {
    this.posthog?.setDistinctId(distinctId)
  }

  private start(): void {
    if (typeof window === 'undefined') return
    if (this.timer == null) {
      this.timer = setInterval(this.boundFlush, this.flushIntervalMs)
    }
    if (!this.listenersBound) {
      window.addEventListener('pagehide', this.boundFlush)
      window.addEventListener('beforeunload', this.boundFlush)
      document.addEventListener('visibilitychange', this.onVisibility)
      this.listenersBound = true
    }
  }

  private readonly onVisibility = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      void this.flush()
    }
  }

  /** Tear down timers/listeners. Flushes any pending items first. */
  destroy(): void {
    if (this.timer != null) {
      clearInterval(this.timer)
      this.timer = null
    }
    if (this.listenersBound && typeof window !== 'undefined') {
      window.removeEventListener('pagehide', this.boundFlush)
      window.removeEventListener('beforeunload', this.boundFlush)
      document.removeEventListener('visibilitychange', this.onVisibility)
      this.listenersBound = false
    }
    void this.flush()
  }

  private stamp(): string {
    return new Date(this.now()).toISOString()
  }

  private queued(): number {
    return this.events.length + this.feedback.length + this.bugs.length
  }

  private trim(): void {
    // Drop oldest if a down backend overflows the queue.
    while (this.queued() > MAX_QUEUE) {
      if (this.events.length) this.events.shift()
      else if (this.feedback.length) this.feedback.shift()
      else if (this.bugs.length) this.bugs.shift()
      else break
    }
  }

  private maybeFlush(): void {
    if (this.queued() >= this.batchSize) void this.flush()
  }

  track(input: TrackInput): void {
    const event = String(input?.event ?? '').trim()
    if (!event) return
    this.events.push({
      event,
      ai_trace_id: input.ai_trace_id ?? null,
      surface: input.surface ?? null,
      workbook_id: input.workbook_id ?? null,
      session_id: this.sessionId,
      props: input.props ?? {},
      client_ts: this.stamp(),
    })
    this.trim()
    this.posthog?.capture(event, {
      ...(input.props ?? {}),
      surface: input.surface ?? undefined,
      ai_trace_id: input.ai_trace_id ?? undefined,
    })
    this.maybeFlush()
  }

  aiFeedback(input: AiFeedbackInput): void {
    const ai_trace_id = String(input?.ai_trace_id ?? '').trim()
    if (!ai_trace_id) return
    if (input.verdict !== 'up' && input.verdict !== 'down') return
    this.feedback.push({
      ai_trace_id,
      verdict: input.verdict,
      note: input.note ?? null,
      surface: input.surface ?? null,
      workbook_id: input.workbook_id ?? null,
      session_id: this.sessionId,
      props: input.props ?? {},
      client_ts: this.stamp(),
    })
    this.trim()
    // Note: free-text `note` is intentionally NOT forwarded to PostHog.
    this.posthog?.capture('ai_feedback', {
      verdict: input.verdict,
      surface: input.surface ?? undefined,
      ai_trace_id,
    })
    this.maybeFlush()
  }

  reportBug(input: BugReportInput): void {
    const surface = String(input?.surface ?? '').trim()
    const message = String(input?.message ?? '').trim()
    if (!surface || !message) return
    this.bugs.push({
      ai_trace_id: input.ai_trace_id ?? null,
      surface,
      message,
      context: input.context ?? {},
      session_id: this.sessionId,
      client_ts: this.stamp(),
    })
    this.trim()
    // Note: free-text `message`/`context` are intentionally NOT forwarded.
    this.posthog?.capture('bug_reported', {
      surface,
      ai_trace_id: input.ai_trace_id ?? undefined,
    })
    this.maybeFlush()
  }

  /** Number of items currently buffered (for tests/diagnostics). */
  pending(): number {
    return this.queued()
  }

  /**
   * Send all buffered items in a single request. Concurrent calls coalesce.
   * On failure the items are re-queued (best-effort) so a transient outage
   * doesn't lose data.
   */
  async flush(): Promise<void> {
    if (this.flushing) return this.flushing
    if (this.queued() === 0) return
    if (!this.transport) return
    if (this.transport.canSendEvents && !this.transport.canSendEvents()) return

    const events = this.events
    const feedback = this.feedback
    const bugs = this.bugs
    this.events = []
    this.feedback = []
    this.bugs = []

    const req: EventsIngestRequest = {}
    if (events.length) req.events = events
    if (feedback.length) req.ai_feedback = feedback
    if (bugs.length) req.bug_reports = bugs

    const transport = this.transport
    this.flushing = (async () => {
      try {
        await transport.ingestEvents(req)
      } catch (err) {
        // A rejected batch can never succeed: re-queuing a 4xx replays the
        // same poisoned payload on every later flush for the whole session.
        // Only transient failures (network, 5xx, timeout 408, throttle 429)
        // earn a retry.
        const status = (err as { status?: unknown })?.status
        const rejected =
          typeof status === 'number' && status >= 400 && status < 500 && status !== 408 && status !== 429
        if (!rejected) {
          // Re-queue at the front so ordering is roughly preserved.
          this.events = [...events, ...this.events]
          this.feedback = [...feedback, ...this.feedback]
          this.bugs = [...bugs, ...this.bugs]
          this.trim()
        }
      } finally {
        this.flushing = null
      }
    })()
    return this.flushing
  }
}
